import springboard from 'springboard';
import {Dialect, Kysely, KyselyPlugin} from 'kysely';

import {DB, WorkspaceType} from './kysely_types';
import {getModeForWorkspaceId, Selectors} from './selectors/selectors';
import {Actions} from './actions/actions';
import {ActionCallOptions, ModuleAPI} from 'springboard/engine/module_api';
import {useQuery, useQueryClient} from '@tanstack/react-query';

import {SerializePlugin} from 'kysely-plugin-serialize';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        Kysely: KyselyModuleReturnValue;
    }
}

import {useSpringboardEngine} from 'springboard/engine/engine';
import {AllModules} from 'springboard/module_registry/module_registry';
import {useCurrentWorkspaceId} from './modules/workspaces_module';

export type KyselyModuleReturnValue = {
    setDialect: (dialect: Dialect, plugins?: KyselyPlugin[]) => void;
    // orm: () => ReturnType<typeof createOrm>;
    db: <DBType = DB>() => Kysely<DBType>;
    selectors: Selectors;
    actions: Actions;
    getActionForWorkspace: <ActionName extends keyof Actions>(workspaceId: string, actionName: ActionName) => Actions[ActionName];
};

export type WorkspaceContext = {
    workspace_id: string | null;
} | {
    mode: 'local' | 'remote';
};

export default (sb: Pick<typeof springboard, 'registerModule'>) => sb.registerModule('Kysely', {}, async (moduleAPI): Promise<KyselyModuleReturnValue> => {
    // let orm: ReturnType<typeof createORMs> | undefined;
    // let ormDatabase: ReturnType<typeof createDB> | undefined;


    console.log('initializing Kysely module');

    let db: Kysely<DB> | undefined;

    const setDialect = (dialect: Dialect, plugins?: KyselyPlugin[]) => {
        // ormDatabase = createDB(dialect);
        // orm = createORMs(ormDatabase);

        db = new Kysely({
            dialect,
            plugins: [
                ...(plugins || []),
                new SerializePlugin({
                    serializer: (value) => {
                        if (value instanceof Date) {
                          return value.toISOString();
                        }

                        return value;
                    },
                    deserializer: (value) => {
                        return value;
                    },
                }),
            ],
        });
    };

    const actions = actionify(Actions, moduleAPI, 'action');
    const selectors = actionify(Selectors, moduleAPI, 'selector');

    console.log('done initializing Kysely module');

    const getActionForWorkspace = <ActionName extends keyof Actions>(workspaceId: string, actionName: ActionName): Actions[ActionName] => {
        return (args: Parameters<Actions[ActionName]>[0]) => {
            const knownWorkspaces = moduleAPI.getModule('Workspaces').knownWorkspacesState.getState();
            const matchingWorkspace = knownWorkspaces.find(w => w.workspace.id === workspaceId);
            const isLocalWorkspace = Boolean(matchingWorkspace && matchingWorkspace.type === 'local');

            const action = actions[actionName];
            if (isLocalWorkspace) {
                return action(args as any, {mode: 'local'}) as any;
            }

            return action(args as any, {mode: 'remote'});
        };
    };

    return {
        setDialect,
        db: <DBType = DB>() => {
            if (!db) {
                throw new Error('Kysely module not initialized, and attempted to access Kysely module property "db".')
            }

            return db as unknown as Kysely<DBType>;
        },
        selectors,
        actions,
        getActionForWorkspace,
    };
});

export const useSelectors = () => {
    const kyselyModule = useModule('Kysely');
    return kyselyModule.selectors;
};

export const useSelectorWithRefresh = <
    QueryName extends keyof Selectors,
    Args extends Parameters<Selectors[QueryName]>[0],
    DefaultValue extends Awaited<ReturnType<Selectors[QueryName]>> | null
>(
    queryName: QueryName,
    args: Args,
    defaultValue: DefaultValue,
    workspaceContext: WorkspaceContext & {ignore?: boolean},
) => {
    type QueryResult = Awaited<ReturnType<Selectors[QueryName]>>;
    type State = DefaultValue extends null ? QueryResult | null : QueryResult;

    const userModule = useModule('User');
    const isOfflineMode = userModule.isOfflineMode.useState();

    const selector = useSelectors()[queryName] as unknown as (args: Args, actionCallOptions?: ActionCallOptions) => Promise<State>;
    const currentWorkspaceId = useCurrentWorkspaceId();

    if (queryName === 'getAllWorkspaces' || queryName === 'getRecentActivityForAllWorkspaces' || queryName === 'searchEverything' || queryName === 'getAllMyStars' || queryName === 'getDashboardData') {
        const query = useQuery({
            queryKey: [queryName, args],
            queryFn: async () => {
                if (workspaceContext.ignore) {
                    return defaultValue;
                }

                if (queryName === 'getAllWorkspaces') {
                    if (isOfflineMode) {
                        return (await selector(args, {mode: 'local'}).catch(e => [])) as State;
                    }

                    if ('mode' in workspaceContext) {
                        if (workspaceContext.mode === 'remote') {
                            return (await selector(args, {mode: 'remote'}).catch(e => [])) as State;
                        } else {
                            return (await selector(args, {mode: 'local'}).catch(e => [])) as State;
                        }
                    }

                    const remoteResults = (await selector(args, {mode: 'remote'}).catch(e => [])) as Awaited<ReturnType<Selectors['getAllWorkspaces']>>;
                    const localResults = await selector(args, {mode: 'local'}).catch(e => []) as Awaited<ReturnType<Selectors['getAllWorkspaces']>>;

                    const combinedResults = remoteResults.concat(localResults);
                    return combinedResults as State;
                } else if (queryName === 'getDashboardData') {
                    const defaultDashboardData: Awaited<ReturnType<Selectors['getDashboardData']>> = {
                        files: [],
                        comments: [],
                        projects: [],
                        attachments: [],
                        users: [],
                        myUserActivity: [],
                        projectRevisions: [],
                    };

                    if (isOfflineMode) {
                        return (await selector(args, {mode: 'local'}).catch(() => defaultDashboardData)) as State;
                    }

                    if ('mode' in workspaceContext) {
                        if (workspaceContext.mode === 'remote') {
                            return (await selector(args, {mode: 'remote'}).catch(() => defaultDashboardData)) as State;
                        } else {
                            return (await selector(args, {mode: 'local'}).catch(() => defaultDashboardData)) as State;
                        }
                    }

                    const remoteResults = await selector(args, {mode: 'remote'}).catch(() => defaultDashboardData) as Awaited<ReturnType<Selectors['getDashboardData']>>;
                    const localResults = await selector(args, {mode: 'local'}).catch(() => defaultDashboardData) as Awaited<ReturnType<Selectors['getDashboardData']>>;

                    if (!remoteResults) {
                        return localResults as State;
                    }

                    for (const key of Object.keys(remoteResults)) {
                        const k = key as ((keyof typeof remoteResults) & (keyof typeof localResults));
                        remoteResults[k] = (remoteResults[k] as any[]).concat(localResults[k]);
                    }

                    return remoteResults as State;
                } else if (queryName === 'getRecentActivityForAllWorkspaces') {
                    if (isOfflineMode) {
                        return (await selector(args, {mode: 'local'}).catch(e => null)) as State;
                    }

                    if ('mode' in workspaceContext) {
                        if (workspaceContext.mode === 'remote') {
                            return (await selector(args, {mode: 'remote'}).catch(e => null)) as State;
                        } else {
                            return (await selector(args, {mode: 'local'}).catch(e => null)) as State;
                        }
                    }

                    const remoteResults = await selector(args, {mode: 'remote'}).catch(e => null) as Awaited<ReturnType<Selectors['getRecentActivityForAllWorkspaces']>>;
                    const localResults = await selector(args, {mode: 'local'}).catch(e => null) as Awaited<ReturnType<Selectors['getRecentActivityForAllWorkspaces']>>;

                    if (!remoteResults) {
                        return localResults as State;
                    }

                    for (const key of Object.keys(remoteResults)) {
                        const k = key as ((keyof typeof remoteResults) & (keyof typeof localResults));
                        remoteResults[k] = (remoteResults[k] as any[]).concat(localResults[k]);
                    }

                    return remoteResults as State;
                } else if (queryName === 'searchEverything') {
                    if (isOfflineMode) {
                        return (await selector(args, {mode: 'local'}).catch(e => null)) as State;
                    }

                    if ('mode' in workspaceContext) {
                        if (workspaceContext.mode === 'remote') {
                            return (await selector(args, {mode: 'remote'}).catch(e => null)) as State;
                        } else {
                            return (await selector(args, {mode: 'local'}).catch(e => null)) as State;
                        }
                    }

                    const remoteResults = await selector(args, {mode: 'remote'}).catch(e => null) as Awaited<ReturnType<Selectors['searchEverything']>>;
                    const localResults = await selector(args, {mode: 'local'}).catch(e => null) as Awaited<ReturnType<Selectors['searchEverything']>>;

                    if (!remoteResults) {
                        return localResults as State;
                    }

                    const combinedResults = remoteResults.concat(localResults);
                    return combinedResults as State;
                } else if (queryName === 'getAllMyStars') {
                    const defaultStarsData: Awaited<ReturnType<Selectors['getAllMyStars']>> = {
                        files: [],
                        projects: [],
                    };

                    if (isOfflineMode) {
                        return (await selector(args, {mode: 'local'}).catch(e => defaultStarsData)) as State;
                    }

                    if ('mode' in workspaceContext) {
                        if (workspaceContext.mode === 'remote') {
                            return (await selector(args, {mode: 'remote'}).catch(e => defaultStarsData)) as State;
                        } else {
                            return (await selector(args, {mode: 'local'}).catch(e => defaultStarsData)) as State;
                        }
                    }

                    const remoteResults = await selector(args, {mode: 'remote'}).catch(e => defaultStarsData) as Awaited<ReturnType<Selectors['getAllMyStars']>>;
                    const localResults = await selector(args, {mode: 'local'}).catch(e => defaultStarsData) as Awaited<ReturnType<Selectors['getAllMyStars']>>;

                    if (!remoteResults) {
                        return localResults as State;
                    }

                    for (const key of Object.keys(remoteResults)) {
                        const k = key as ((keyof typeof remoteResults) & (keyof typeof localResults));
                        remoteResults[k] = (remoteResults[k] as any[]).concat(localResults[k]);
                    }

                    return remoteResults as State;
                }
            },
            initialData: defaultValue as State,
        });

        return [query.data as State, query.refetch, query.isLoading, query.error] as const;
    }


    let mode = ('mode' in workspaceContext) ? workspaceContext.mode : undefined;
    const workspaceId = ('workspace_id' in workspaceContext) ? workspaceContext.workspace_id : currentWorkspaceId;
    if (!mode) {
        const workspacesModule = useModule('Workspaces');
        const knownWorkspaces = workspacesModule.knownWorkspacesState.useState();
        const matchingWorkspace = knownWorkspaces.find(w => w.workspace.id === workspaceId);
        const isLocalWorkspace = Boolean(matchingWorkspace && matchingWorkspace.type === 'local');
        mode = isLocalWorkspace ? 'local' : 'remote';
    }

    const query = useQuery({
        queryKey: workspaceId ? [workspaceId, queryName, args] : [queryName, args],
        queryFn: async () => {
            if (workspaceContext.ignore) {
                return defaultValue;
            }

            const actionCallOptions: ActionCallOptions | undefined = {mode};
            return selector(args as any, actionCallOptions);
        },
        initialData: defaultValue as State,
    });

    return [query.data as State, query.refetch, query.isLoading, query.error] as const;
};

export const useActions = () => {
    const kyselyModule = useModule('Kysely');
    return kyselyModule.actions;
};

export const useKysely = () => {
    const kyselyModule = useModule('Kysely');
    return kyselyModule.db();
};

export const useAction = <ActionName extends keyof Actions>(actionName: ActionName): Actions[ActionName] => {
    const kyselyModule = useModule('Kysely');
    const originalAction = kyselyModule.actions[actionName];
    const currentWorkspaceId = useCurrentWorkspaceId();

    if (actionName === 'createWorkspace') {
        return ((args: Parameters<Actions['createWorkspace']>[0]) => {
            const workspaceType = args.type;
            const isLocalWorkspace = workspaceType === WorkspaceType.LOCAL;

            const innerAction = kyselyModule.actions.createWorkspace;
            const actionCallOptions = {mode: isLocalWorkspace ? 'local' : 'remote'} as const;

            return innerAction(args, actionCallOptions);
        }) as unknown as typeof originalAction;
    }

    const workspacesModule = useModule('Workspaces');
    const knownWorkspaces = workspacesModule.knownWorkspacesState.useState();

    if (actionName === 'addFileToWorkspace') {
        return ((args: Parameters<Actions['addFileToWorkspace']>[0]) => {
            const workspaceId = args.workspace_id;
            const matchingWorkspace = knownWorkspaces.find(w => w.workspace.id === workspaceId);
            const isLocalWorkspace = Boolean(matchingWorkspace && matchingWorkspace.type === 'local');

            const innerAction = kyselyModule.actions.addFileToWorkspace;
            const actionCallOptions = {mode: isLocalWorkspace ? 'local' : 'remote'} as const;

            return innerAction(args, actionCallOptions);
        }) as unknown as typeof originalAction;
    }

    return ((args: Parameters<typeof originalAction>[0], workspaceContext: WorkspaceContext) => {
        let mode = ('mode' in workspaceContext) ? workspaceContext.mode : undefined;
        if (!mode) {
            const workspaceId = ('workspace_id' in workspaceContext) ? workspaceContext.workspace_id : currentWorkspaceId;
            const matchingWorkspace = knownWorkspaces.find(w => w.workspace.id === workspaceId);
            const isLocalWorkspace = Boolean(matchingWorkspace && matchingWorkspace.type === 'local');
            mode = isLocalWorkspace ? 'local' : 'remote';
        }

        const originalActionWithActionCallOptions = originalAction as unknown as WithCallOptions<typeof originalAction>;
        const actionCallOptions: ActionCallOptions = {mode};

        return originalActionWithActionCallOptions(args as any, actionCallOptions);
    }) as unknown as typeof originalAction;
};

function actionify<T extends {[key: string]: any}>(
    actionsClass: new (moduleAPI: ModuleAPI) => T,
    moduleAPI: ModuleAPI,
    namespacePrefix: string = ''
): T {
    const actionsInstance = new actionsClass(moduleAPI);

    // Create a new object to store the actionified methods
    const actionifiedMethods: Partial<T> = {};

    for (const key of Object.keys(actionsInstance) as (keyof T)[]) {
        const value = actionsInstance[key];

        // wrap functions using createAction
        if (typeof value === 'function' && !(typeof key === 'string' && key.startsWith('_'))) {
            const actionName = namespacePrefix
                ? `${namespacePrefix}.${String(key)}`
                : String(key);

            const action = moduleAPI.createAction(
                actionName,
                {},
                value.bind(actionsInstance),
            );

            const actionWrapper: typeof action = async (args, workspaceContext) => {
                if (!workspaceContext || !('workspace_id' in workspaceContext)) {
                    return action(args, workspaceContext);
                }

                const workspacesModule = moduleAPI.deps.module.moduleRegistry.getModule('Workspaces');
                const mode = getModeForWorkspaceId(workspacesModule, {workspace_id: workspaceContext.workspace_id as string});
                return action(args, {mode});
            }

            actionifiedMethods[key] = actionWrapper as T[typeof key];
        } else {
            // copy non-function properties as-is
            actionifiedMethods[key] = value;
        }
    }

    // Return the fully actionified object
    return actionifiedMethods as T;
}

export const useModule = <ModuleId extends keyof AllModules>(moduleId: ModuleId): AllModules[ModuleId] => {
    const engine = useSpringboardEngine();
    return engine.moduleRegistry.getModule(moduleId);
};

type AddParameter<
    TFunction extends (arg: any) => any,
    TNewParam
> = TFunction extends (arg: infer TArg) => infer TReturn
    ? (arg: TArg, newParam?: TNewParam) => TReturn
    : never;

export type WithCallOptions<TFunction extends (...args: any[]) => any> = AddParameter<TFunction, ActionCallOptions>;
